import { PDFDocument, PDFFont, rgb } from 'pdf-lib';
import { embedBodyFonts, embedStandardFallbackFonts } from './pdfFonts';
import type { ImageFitMode, PdfExportQuality } from './pdfExportQuality';
import { layoutForQuality } from './pdfExportQuality';
import { isJpeg, isPng, webpToPngBytes } from './pdfImageBytes';
import type { PageDims } from './pdfPageDims';

export interface ImagePdfOptions {
  quality: PdfExportQuality;
  pageDims: PageDims;
  fitMode: 'fitPage' | 'fitWidth' | 'original';
  centerImage: boolean;
  preserveExifOrientation: boolean;
  background: 'white' | 'transparent';
}

async function embedRaster(
  doc: PDFDocument,
  bytes: Uint8Array,
  mimeGuess: string,
  preserveExifOrientation: boolean,
): Promise<Awaited<ReturnType<PDFDocument['embedPng']>>> {
  const orientation = preserveExifOrientation ? ('from-image' as const) : undefined;
  const lower = mimeGuess.toLowerCase();
  if (lower.includes('webp')) {
    return doc.embedPng(await webpToPngBytes(bytes));
  }
  if (isPng(bytes)) return doc.embedPng(bytes);
  if (isJpeg(bytes) || lower.includes('jpeg') || lower.includes('jpg')) {
    const blob = new Blob([new Uint8Array(bytes)], { type: 'image/jpeg' });
    const bmp = orientation ? await createImageBitmap(blob, { imageOrientation: orientation }) : await createImageBitmap(blob);
    const c = document.createElement('canvas');
    c.width = bmp.width;
    c.height = bmp.height;
    const ctx = c.getContext('2d');
    if (!ctx) {
      bmp.close();
      return doc.embedJpg(bytes);
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bmp, 0, 0);
    bmp.close();
    const jpegBlob = await new Promise<Blob>((res, rej) => {
      c.toBlob((b) => (b ? res(b) : rej(new Error('toBlob'))), 'image/jpeg', 0.96);
    });
    return doc.embedJpg(new Uint8Array(await jpegBlob.arrayBuffer()));
  }
  try {
    return await doc.embedPng(bytes);
  } catch {
    try {
      return await doc.embedJpg(bytes);
    } catch {
      const blob = new Blob([new Uint8Array(bytes)], { type: mimeGuess || 'image/*' });
      const bmp = orientation ? await createImageBitmap(blob, { imageOrientation: orientation }) : await createImageBitmap(blob);
      const c = document.createElement('canvas');
      c.width = bmp.width;
      c.height = bmp.height;
      const ctx = c.getContext('2d');
      if (!ctx) {
        bmp.close();
        throw new Error('Cannot decode image');
      }
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(bmp, 0, 0);
      bmp.close();
      const pngBlob = await new Promise<Blob>((res, rej) =>
        c.toBlob((b) => (b ? res(b) : rej(new Error('toBlob'))), 'image/png'),
      );
      return doc.embedPng(new Uint8Array(await pngBlob.arrayBuffer()));
    }
  }
}

function computeDrawSize(
  imgW: number,
  imgH: number,
  fit: ImageFitMode,
  maxW: number,
  maxH: number,
): { w: number; h: number } {
  if (fit === 'original') {
    const wPt = imgW * (72 / 96);
    const hPt = imgH * (72 / 96);
    if (wPt <= maxW && hPt <= maxH) return { w: wPt, h: hPt };
    const r = Math.min(maxW / wPt, maxH / hPt);
    return { w: wPt * r, h: hPt * r };
  }
  if (fit === 'fitWidth') {
    const r = Math.min(1, maxW / imgW);
    return { w: imgW * r, h: imgH * r };
  }
  const r = Math.min(maxW / imgW, maxH / imgH, 1);
  return { w: imgW * r, h: imgH * r };
}

function drawFooter(page: ReturnType<PDFDocument['getPage']>, index: number, total: number, font: PDFFont, margin: number): void {
  const label = `Page ${index + 1} of ${total}`;
  const size = 8.5;
  const w = font.widthOfTextAtSize(label, size);
  const pw = page.getWidth();
  page.drawText(label, {
    x: pw - margin - w,
    y: margin * 0.55,
    size,
    font,
    color: rgb(0.55, 0.55, 0.58),
  });
}

export async function convertRasterBytesToPdf(bytes: Uint8Array, mimeType: string, opts: ImagePdfOptions): Promise<Uint8Array> {
  const layout = layoutForQuality(opts.quality);
  const doc = await PDFDocument.create();
  let fonts;
  try {
    fonts = await embedBodyFonts(doc);
  } catch {
    fonts = await embedStandardFallbackFonts(doc);
  }

  const { widthPt: PAGE_W, heightPt: PAGE_H } = opts.pageDims;
  const page = doc.addPage([PAGE_W, PAGE_H]);

  if (opts.background === 'white') {
    page.drawRectangle({
      x: 0,
      y: 0,
      width: PAGE_W,
      height: PAGE_H,
      color: rgb(1, 1, 1),
    });
  }

  const img = await embedRaster(doc, bytes, mimeType || 'image/jpeg', opts.preserveExifOrientation);
  const naturalW = img.width;
  const naturalH = img.height;

  const margin = layout.margin;
  const maxW = PAGE_W - 2 * margin;
  const maxH = PAGE_H - 2 * margin - layout.footerReserved;

  const fit = opts.fitMode as ImageFitMode;
  const { w, h } = computeDrawSize(naturalW, naturalH, fit, maxW, maxH);

  let xLeft = margin;
  if (opts.centerImage) {
    xLeft = margin + (maxW - w) / 2;
  }

  const imgBottomY = opts.centerImage
    ? margin + layout.footerReserved + (maxH - h) / 2
    : PAGE_H - margin - h;

  page.drawImage(img, { x: xLeft, y: imgBottomY, width: w, height: h });

  const pages = doc.getPages();
  pages.forEach((p, i) => drawFooter(p, i, pages.length, fonts.regular, layout.margin));

  return doc.save();
}

/** Rasterize SVG text to PNG bytes via browser (vector graphic → raster for embedding). */
export async function rasterizeSvgToPng(svgSource: string): Promise<Uint8Array> {
  const blob = new Blob([svgSource], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((res, rej) => {
      img.onload = () => res();
      img.onerror = () => rej(new Error('SVG raster failed'));
      img.src = url;
    });
    const w = img.naturalWidth || img.width || 400;
    const h = img.naturalHeight || img.height || 400;
    const c = document.createElement('canvas');
    c.width = Math.min(Math.max(w, 1), 4096);
    c.height = Math.min(Math.max(h, 1), 4096);
    const ctx = c.getContext('2d');
    if (!ctx) throw new Error('Canvas unavailable');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.drawImage(img, 0, 0, c.width, c.height);
    const pngBlob = await new Promise<Blob>((res, rej) =>
      c.toBlob((b) => (b ? res(b) : rej(new Error('toBlob'))), 'image/png'),
    );
    return new Uint8Array(await pngBlob.arrayBuffer());
  } finally {
    URL.revokeObjectURL(url);
  }
}
